import { useCallback, useEffect, useState } from "react";
import { Copy, PlugZap, Unplug } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { TEAL } from "@/lib/copilot";
import { Loader, PageHeader } from "@/components/common";

export default function ConnectedAI() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/oauth/connections").then(({ data: d }) => setData(d)).catch((e) => setData({ error: errMsg(e) })), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loader />;
  const disconnect = (c) => api.delete(`/oauth/connections/${c.id}`).then(() => { toast.success(`${c.client_name} disconnected`); load(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div data-testid="connected-ai-page">
      <PageHeader eyebrow="My account" title="Connected AI" rgb={TEAL} testid="connected-ai-title" />
      {data.error ? <p className="q-error" data-testid="connected-ai-error">{data.error}</p> : (
        <div className="max-w-2xl space-y-4">
          <section className="q-card">
            <p className="flex items-center gap-2 font-display text-lg font-extrabold text-q-text"><PlugZap className="h-5 w-5" style={{ color: `rgb(${TEAL})` }} /> Use The Queue from ChatGPT or Claude</p>
            <p className="mt-1 text-sm text-q-muted">Add a custom connector with this URL, then sign in and pick your family hub. It acts with your own permissions.</p>
            <div className="mt-3 flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded-lg bg-q-raised px-3 py-2.5 text-xs text-q-text" data-testid="mcp-url">{data.mcp_url}</code>
              <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => navigator.clipboard?.writeText(data.mcp_url).then(() => toast.success("Copied"))} data-testid="mcp-url-copy"><Copy className="h-4 w-4" /> Copy</button>
            </div>
          </section>
          <section className="q-card">
            <h2 className="mb-3 font-display text-lg font-extrabold text-q-text">Connections</h2>
            <ul className="space-y-2" data-testid="connections-list">
              {data.connections.length === 0 && <li className="text-sm text-q-faint" data-testid="connections-empty">No AI assistants connected.</li>}
              {data.connections.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-q-border bg-q-surface px-4 py-3" data-testid={`connection-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-q-text" data-testid="connection-name">{c.client_name}</p>
                    <p className="text-xs text-q-muted">{c.hubs.map((h) => h.name).join(", ")} · connected {new Date(c.created_at).toLocaleDateString()}{c.last_used_at ? ` · last used ${new Date(c.last_used_at).toLocaleString()}` : ""}</p>
                  </div>
                  <button className="q-btn q-btn-danger min-h-[44px]" onClick={() => disconnect(c)} data-testid={`connection-disconnect-${c.id}`}><Unplug className="h-4 w-4" /> Disconnect</button>
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </div>
  );
}
